/**
 * React-Admin Format API Client
 * 
 * Centralized client for all React-Admin format API requests.
 * The backend uses FastAPI with a custom ReactAdmin wrapper that provides
 * standardized CRUD endpoints at /{resource}/ra/{operation}
 * 
 * @see backend/fastapi_react_admin/react_admin.py
 */

import { api } from './api'

// ============================================================================
// File Upload Helpers
// ============================================================================

/**
 * Convert a File object to a base64 data URL string
 */
const convertFileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

/**
 * Type guard to check if a value is a file upload object (has rawFile property)
 */
interface FileUploadObject {
  rawFile: File
  src?: string
  title?: string
}

const isFileUploadObject = (value: unknown): value is FileUploadObject => {
  return (
    value !== null &&
    typeof value === 'object' &&
    'rawFile' in value &&
    (value as FileUploadObject).rawFile instanceof File
  )
}

/**
 * Recursively process an object to find and convert File objects to base64.
 * This handles the react-admin ImageInput format where files are stored as:
 * { rawFile: File, src: blobUrl, title: filename }
 * 
 * After processing, the object becomes:
 * { src: base64DataUrl, title: filename }
 */
const handleFileUpload = async (data: Record<string, unknown>): Promise<void> => {
  const promises: Promise<void>[] = []

  const recursiveConvert = (obj: Record<string, unknown>) => {
    for (const key of Object.keys(obj)) {
      const value = obj[key]

      if (isFileUploadObject(value)) {
        // Found a file upload object, convert to base64
        promises.push(
          convertFileToBase64(value.rawFile).then((base64Image) => {
            obj[key] = {
              src: base64Image,
              title: value.rawFile.name,
            }
          })
        )
      } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Recurse into nested objects
        recursiveConvert(value as Record<string, unknown>)
      } else if (Array.isArray(value)) {
        // Handle arrays - recurse into each object element
        for (const item of value) {
          if (item !== null && typeof item === 'object') {
            recursiveConvert(item as Record<string, unknown>)
          }
        }
      }
    }
  }

  recursiveConvert(data)
  await Promise.all(promises)
}

// ============================================================================
// Types
// ============================================================================

export interface RAListParams {
  page?: number
  perPage?: number
  sort?: { field: string; order: 'ASC' | 'DESC' }
  filter?: Record<string, unknown>
}

export interface RAListResponse<T> {
  data: T[]
  total: number
}

// ============================================================================
// Core Client
// ============================================================================

/**
 * Centralized React-Admin API client
 * 
 * @example
 * ```typescript
 * // Fetch a paginated list
 * const { data, total } = await raClient.getList<Conversation>('conversations', {
 *   page: 1,
 *   perPage: 25,
 *   sort: { field: 'updated_at', order: 'DESC' },
 *   filter: { status: 'active' }
 * })
 * 
 * // Fetch a single record
 * const conversation = await raClient.getOne<Conversation>('conversations', 123)
 * 
 * // Fetch multiple records by IDs
 * const assistants = await raClient.getMany<Assistant>('assistants', [1, 2, 3])
 * ```
 */
export const raClient = {
  /**
   * Fetch a paginated list of records
   * 
   * Uses POST to /{resource}/ra/getList with react-admin query format:
   * - sort: JSON array ["field", "ASC"|"DESC"]
   * - range_: JSON array [start, end] (note the underscore!)
   * - filter: JSON object with filter conditions
   */
  async getList<T>(
    resource: string,
    params: RAListParams = {}
  ): Promise<RAListResponse<T>> {
    const { 
      page = 1, 
      perPage = 25, 
      sort = { field: 'id', order: 'DESC' }, 
      filter = {} 
    } = params
    
    // Calculate range from page and perPage
    const start = (page - 1) * perPage
    const end = start + perPage - 1

    // Build query params in react-admin format
    const queryParams = new URLSearchParams()
    queryParams.set('sort', JSON.stringify([sort.field, sort.order]))
    queryParams.set('range_', JSON.stringify([start, end])) // Note: range_ with underscore!
    queryParams.set('filter', JSON.stringify(filter))

    const response = await api.post(`/${resource}/ra/getList?${queryParams.toString()}`)
    const { data, total } = response.data

    return {
      data: data || [],
      total: total || 0,
    }
  },

  /**
   * Fetch a single record by ID
   * 
   * Uses POST to /{resource}/ra/getOne/{id}
   */
  async getOne<T>(resource: string, id: string | number): Promise<T> {
    const response = await api.post(`/${resource}/ra/getOne/${id}`)
    return response.data.data || response.data
  },

  /**
   * Fetch multiple records by IDs
   * 
   * Uses POST to /{resource}/ra/getMany with filter={id: [...]}
   * Falls back to getList with filter if getMany not available
   */
  async getMany<T>(resource: string, ids: (string | number)[]): Promise<T[]> {
    if (!ids.length) return []
    
    try {
      // Try getMany first (not all resources support this)
      // Backend expects filter parameter with id array, not ids parameter
      const params = new URLSearchParams()
      params.set('filter', JSON.stringify({ id: ids }))
      
      const response = await api.post(`/${resource}/ra/getMany?${params.toString()}`)
      return response.data.data || response.data
    } catch {
      // Fallback to getList with id filter
      const result = await this.getList<T>(resource, {
        page: 1,
        perPage: ids.length,
        filter: { id: ids },
      })
      return result.data
    }
  },

  /**
   * Create a new record
   * 
   * Uses POST to /{resource}/ra/create
   * Automatically converts File objects to base64 before sending
   */
  async create<T>(resource: string, data: Partial<T>): Promise<T> {
    // Process any file uploads (converts File objects to base64)
    await handleFileUpload(data as Record<string, unknown>)
    const response = await api.post(`/${resource}/ra/create`, data)
    return response.data.data || response.data
  },

  /**
   * Update an existing record
   * 
   * Uses POST to /{resource}/ra/update/{id}
   * Automatically converts File objects to base64 before sending
   */
  async update<T>(resource: string, id: string | number, data: Partial<T>): Promise<T> {
    // Process any file uploads (converts File objects to base64)
    await handleFileUpload(data as Record<string, unknown>)
    const response = await api.post(`/${resource}/ra/update/${id}`, data)
    return response.data.data || response.data
  },

  /**
   * Delete a record
   * 
   * Uses POST to /{resource}/ra/delete/{id}
   */
  async delete(resource: string, id: string | number): Promise<void> {
    await api.post(`/${resource}/ra/delete/${id}`)
  },
}

export default raClient
